import type { NextPage } from "next";
import { SwatchIcon, WalletIcon } from "@heroicons/react/24/outline";
import { getProductGuideUrl } from "utils/notion";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import ProtectedPage from "@components/base-layout/ProtectedPage";
import CardWidget from "@components/ui/Widget/CardWidget";
import { trpc } from "@utils";
import WidgetLoading from "@components/ui/Widget/WidgetLoading";
import { RequestConnectionWidget } from "@components/request-finance/RequestConnectWidget";
import { Widget } from "@ui";
import TreasuryStartPage from "@components/base-layout/EmptyAccountView";
import { useSession } from "next-auth/react";
import useHandleRoRedirect from "hooks/useHandleRoRedirect";

const Dynamic30DaysFlowChart = dynamic(
    () => import("@components/dashboard/widgets/Last30DaysFiatFlowsChart"),
    {
        ssr: false,
        loading: () => <WidgetLoading className="h-96" />,
    },
);

const DynamicPortfolioWidget = dynamic(
    () => import("@components/dashboard/widgets/PortfolioWidget"),
    {
        ssr: false,
        loading: () => (
            <>
                <WidgetLoading className="h-96" />
                <WidgetLoading className="h-96" />
            </>
        ),
    },
);

const Home: NextPage = () => {
    const {
        data: count,
        isLoading,
        isError,
    } = trpc.trackedWallet.countForTeam.useQuery();

    const {
        data: countConnectionsForTeam,
        isLoading: isCountConnectionsLoading,
        isError: isCountConnectionsError,
    } = trpc.team.countConnectionsForTeam.useQuery();

    const showLoading = isLoading || isCountConnectionsLoading;
    const showError = isError || isCountConnectionsError;

    const { data } = useSession();

    useHandleRoRedirect();

    const title = useMemo(() => {
        return (
            <span className="text-4xl font-semibold">
                {`👋 Hey 
                ${data?.user?.companyDetails?.firstName ?? data?.user.email}`}
            </span>
        );
    }, [data]);

    if (countConnectionsForTeam == 0) {
        return (
            <ProtectedPage title={title}>
                <Widget paddingless>
                    <TreasuryStartPage />
                </Widget>
            </ProtectedPage>
        );
    }

    return (
        <ProtectedPage title={title}>
            <div className="mt-10 grid grid-cols-2 gap-8 xl:grid-cols-3">
                <Dynamic30DaysFlowChart />
                <DynamicPortfolioWidget />

                <CardWidget
                    icon={
                        <WalletIcon
                            className="mx-auto size-9"
                            stroke="url(#consola-gradient)"
                        />
                    }
                    iconTitle="Wallets"
                    title="Linked Wallets"
                    href="/wallets"
                    description={`You have ${
                        showLoading || showError ? "..." : count
                    } wallets linked`}
                    infoHref={getProductGuideUrl("wallets")}
                />
                <CardWidget
                    icon={
                        <SwatchIcon
                            className="mx-auto size-9"
                            stroke="url(#consola-gradient)"
                        />
                    }
                    iconTitle="Accounts"
                    title="Chart of Accounts"
                    href="/chart-of-accounts"
                    description="Manage account types"
                    infoHref={getProductGuideUrl("account-types")}
                />
                <RequestConnectionWidget />
            </div>
        </ProtectedPage>
    );
};

export default Home;
